/**
 * Las cuentas de un gráfico, separadas de su dibujo.
 *
 * Viven aparte del componente por una razón práctica: **jsdom no tiene
 * layout**, así que lo único que un test unitario puede afirmar de un gráfico
 * es su geometría calculada. Con las escalas acá, "el punto de la semana con
 * más deuda queda más arriba que el resto" es un test de función pura; dentro
 * del componente sería una promesa sin verificar.
 */

/** El área de dibujo, sin los márgenes de los ejes. */
export interface Lienzo {
  ancho: number
  alto: number
  margen: { arriba: number; derecha: number; abajo: number; izquierda: number }
}

export const LIENZO: Lienzo = {
  ancho: 640,
  alto: 180,
  // Los márgenes dejan lugar a las etiquetas de los ejes. El derecho es más
  // ancho que el izquierdo a propósito: ahí va la etiqueta directa del último
  // punto, que es la que evita tener que leer la leyenda.
  margen: { arriba: 12, derecha: 44, abajo: 22, izquierda: 32 },
}

export function anchoUtil(lienzo: Lienzo = LIENZO): number {
  return lienzo.ancho - lienzo.margen.izquierda - lienzo.margen.derecha
}

export function altoUtil(lienzo: Lienzo = LIENZO): number {
  return lienzo.alto - lienzo.margen.arriba - lienzo.margen.abajo
}

/**
 * El tope del eje Y, redondeado a un número limpio.
 *
 * Nunca ajusta el eje al dato exacto: un máximo de 13 con el eje en 13 deja el
 * pico pegado al borde y sin una marca contra la cual leerlo. Y **siempre
 * incluye el cero**: una serie de conteos con el eje arrancando en 8 exagera
 * cualquier variación, que es la forma más común de mentir con un gráfico sin
 * escribir un solo número falso.
 */
export function topeY(valores: readonly number[], minimo = 4): number {
  const max = Math.max(0, ...valores.filter((v) => Number.isFinite(v)))
  const objetivo = Math.max(minimo, max)
  const magnitud = 10 ** Math.floor(Math.log10(objetivo))
  // Escalones "limpios" dentro de cada magnitud. Salteados a propósito: 2.5 o
  // 7.5 son números redondos para una calculadora y no para un ojo.
  const escalones = [1, 1.5, 2, 3, 4, 5, 6, 8, 10].map((m) => m * magnitud)

  const tope = escalones.find((e) => e >= objetivo) ?? magnitud * 10
  // Si el tope cae JUSTO en el máximo, el pico queda pegado al borde de arriba
  // y sin ninguna marca contra la cual leerlo: se sube un escalón.
  return tope > max ? tope : (escalones.find((e) => e > max) ?? max * 2)
}

/** Los valores de las marcas del eje Y: cero, el tope y el medio. */
export function ticksY(tope: number): number[] {
  return tope % 2 === 0 ? [0, tope / 2, tope] : [0, tope]
}

/** `x` del punto `i` de `n`, repartidos de borde a borde del área útil. */
export function posX(i: number, n: number, lienzo: Lienzo = LIENZO): number {
  if (n <= 1) return lienzo.margen.izquierda + anchoUtil(lienzo) / 2
  return lienzo.margen.izquierda + (anchoUtil(lienzo) * i) / (n - 1)
}

/** `y` de un valor, con el cero abajo. */
export function posY(valor: number, tope: number, lienzo: Lienzo = LIENZO): number {
  const proporcion = tope === 0 ? 0 : valor / tope
  return lienzo.margen.arriba + altoUtil(lienzo) * (1 - proporcion)
}

/**
 * El `d` de una línea que **puede tener huecos**.
 *
 * Un `null` no se interpola: corta el trazo y lo retoma después. Es la
 * diferencia entre "esa semana el cumplimiento fue bajo" y "esa semana no se
 * cerró nada con plazo, así que no hay cumplimiento que dibujar" — unir los dos
 * puntos vecinos inventaría una línea que pasa por un valor que nadie midió.
 */
export function pathDeLinea(
  valores: readonly (number | null)[],
  tope: number,
  lienzo: Lienzo = LIENZO,
): string {
  const partes: string[] = []
  let dibujando = false

  valores.forEach((valor, i) => {
    if (valor === null || !Number.isFinite(valor)) {
      dibujando = false
      return
    }
    const x = posX(i, valores.length, lienzo)
    const y = posY(valor, tope, lienzo)
    partes.push(`${dibujando ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`)
    dibujando = true
  })

  return partes.join(' ')
}

/** El `d` de un área cerrada contra la línea de base. */
export function pathDeArea(
  valores: readonly number[],
  tope: number,
  lienzo: Lienzo = LIENZO,
): string {
  if (valores.length === 0) return ''
  const base = posY(0, tope, lienzo)
  const puntos = valores.map(
    (valor, i) =>
      `${posX(i, valores.length, lienzo).toFixed(1)} ${posY(valor, tope, lienzo).toFixed(1)}`,
  )
  const primero = posX(0, valores.length, lienzo).toFixed(1)
  const ultimo = posX(valores.length - 1, valores.length, lienzo).toFixed(1)
  return `M${primero} ${base} L${puntos.join(' L')} L${ultimo} ${base} Z`
}

/**
 * El índice más cercano a una posición horizontal, para el hover.
 *
 * El gráfico no espera que le apunten al punto: alcanza con estar a su altura.
 * Un objetivo de 4px de radio es imposible de acertar con el mouse en
 * movimiento, y en una serie de doce semanas la franja vertical entera es una
 * respuesta sin ambigüedad.
 */
export function indiceMasCercano(x: number, n: number, lienzo: Lienzo = LIENZO): number {
  if (n <= 1) return 0
  const relativo = (x - lienzo.margen.izquierda) / anchoUtil(lienzo)
  return Math.min(n - 1, Math.max(0, Math.round(relativo * (n - 1))))
}
